import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import {
  FaAddressCard,
  FaPlus,
  FaSearch,
  FaPhoneAlt,
  FaWhatsapp,
  FaEdit,
  FaTrash,
  FaTimes,
} from 'react-icons/fa';
import { crmContactApi } from '../../api/crmApi';
import AppDropdown from '../../components/AppDropdown';
import './CRM.css';

const fallbackContacts = [
  { id: 1, name: 'Ankit Mehta', designation: 'Director', company: 'Mehta Oxygen Pvt. Ltd.', companyId: 1, mobile: '9876543210', email: 'ankit@mehtaoxygen.in', whatsapp: '9876543210', preferredContact: 'WhatsApp', isPrimary: true },
  { id: 2, name: 'Rajesh Kumar', designation: 'Purchase Manager', company: 'Kumar Industrial Gases', companyId: 2, mobile: '9811122233', email: 'rajesh@kumarindgas.com', whatsapp: '', preferredContact: 'Call', isPrimary: true },
  { id: 3, name: 'Sunita Devi', designation: 'Owner', company: 'Devi Gas Agency', companyId: 3, mobile: '9765544332', email: 'sunita@devigas.in', whatsapp: '9765544332', preferredContact: 'Email', isPrimary: true },
  { id: 4, name: 'Mohammed Ali', designation: 'Proprietor', company: 'Ali Welding Works', companyId: 4, mobile: '9898989898', email: 'ali@aliwelding.com', whatsapp: '', preferredContact: 'Call', isPrimary: true },
  { id: 5, name: 'Pooja Sharma', designation: 'Managing Director', company: 'Sharma Medical Gas', companyId: 5, mobile: '9777666555', email: 'pooja@sharmamedical.in', whatsapp: '9777666555', preferredContact: 'WhatsApp', isPrimary: true },
];

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

export default function CRMContacts() {
  const [contacts, setContacts] = useState(fallbackContacts);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmContactApi.getAll({ size: 200 });
      if (Array.isArray(res.data)) setContacts(res.data);
      else if (res.data?.content) setContacts(res.data.content);
    } catch { /* fallback */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setForm({ name: '', designation: '', company: '', mobile: '', email: '', whatsapp: '', preferredContact: 'Call', isPrimary: false }); setModal('new'); };
  const openEdit = (c) => { setForm({ ...c }); setModal(c); };

  const save = async () => {
    if (!form.name?.trim() || !form.mobile?.trim()) { toast.error('Name and mobile are required.'); return; }
    try {
      if (modal === 'new') {
        const res = await crmContactApi.create(form);
        setContacts((p) => [...p, res.data || { ...form, id: Date.now() }]);
        toast.success('Contact created.');
      } else {
        await crmContactApi.update(modal.id, form);
        setContacts((p) => p.map((c) => c.id === modal.id ? { ...c, ...form } : c));
        toast.success('Contact updated.');
      }
    } catch { toast.error('Failed to save contact.'); }
    setModal(null);
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete contact "${c.name}"?`)) return;
    try { await crmContactApi.delete(c.id); setContacts((p) => p.filter((x) => x.id !== c.id)); toast.success('Contact deleted.'); }
    catch { toast.error('Failed to delete contact.'); }
  };

  const filtered = contacts.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q) ||
      (c.mobile || '').includes(q) || (c.email || '').toLowerCase().includes(q) || (c.designation || '').toLowerCase().includes(q);
  });

  const fields = [
    { k: 'name', label: 'Name', req: true }, { k: 'designation', label: 'Designation' },
    { k: 'company', label: 'Company' }, { k: 'mobile', label: 'Mobile', req: true },
    { k: 'email', label: 'Email', type: 'email' }, { k: 'whatsapp', label: 'WhatsApp Number' },
    { k: 'preferredContact', label: 'Preferred Contact', type: 'select', options: ['Call', 'WhatsApp', 'Email'] },
    { k: 'isPrimary', label: 'Primary Contact', type: 'checkbox' },
  ];

  return (
    <div className="crm-page crm-contacts-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon" aria-hidden="true"><FaAddressCard /></div>
          <div>
            <h1 className="crm-title">Contacts</h1>
            <p className="crm-subtitle">People at your companies — call, email & WhatsApp from here</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button type="button" className="crm-btn crm-btn-primary" onClick={openCreate}>
            <FaPlus aria-hidden="true" /> New Contact
          </button>
        </div>
      </div>

      <div className="crm-filters">
        <div className="crm-search-box">
          <FaSearch className="crm-search-icon" aria-hidden="true" />
          <input
            type="text"
            aria-label="Search contacts"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, company, mobile..."
            autoComplete="off"
            spellCheck="false"
          />
        </div>
        <button type="button" className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => setSearch('')}>Clear</button>
      </div>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead><tr><th>Name</th><th>Designation</th><th>Company</th><th>Mobile</th><th>Email</th><th>Preferred</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, r) => (
                <tr key={`skeleton-${r}`}>
                  {Array.from({ length: 7 }).map((__, cc) => (
                    <td key={cc}><SkeletonBlock width={cc === 0 ? 120 : '80%'} height={16} radius={6} /></td>
                  ))}
                </tr>
              ))
            ) : (
              <>
            {filtered.map((c) => (
              <tr key={c.id}>
                <td><strong>{c.name}</strong>{c.isPrimary && <span className="crm-badge crm-badge-won" style={{ marginLeft: 8 }}>Primary</span>}</td>
                <td>{c.designation || '—'}</td>
                <td>{c.company || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{c.mobile}</td>
                <td style={{ fontSize: '0.79rem' }}>{c.email || '—'}</td>
                <td>{c.preferredContact || '—'}</td>
                <td>
                  <div className="crm-actions">
                    <button type="button" className="crm-icon-btn call" title="Call" aria-label={`Call ${c.name}`} onClick={() => { window.location.href = `tel:${c.mobile}`; }}><FaPhoneAlt aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn whatsapp" title="WhatsApp" aria-label={`WhatsApp ${c.name}`} onClick={() => { window.open(`https://wa.me/91${c.whatsapp || c.mobile}`, '_blank'); }}><FaWhatsapp aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn" title="Edit" aria-label={`Edit ${c.name}`} onClick={() => openEdit(c)}><FaEdit aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn lost" title="Delete" aria-label={`Delete ${c.name}`} onClick={() => remove(c)}><FaTrash aria-hidden="true" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={7}>
                <div className="crm-empty">
                  <div className="crm-empty-icon" aria-hidden="true"><FaAddressCard /></div>
                  <div className="crm-empty-title">No contacts found</div>
                  <div className="crm-empty-desc">Create contacts to reach decision makers at your companies.</div>
                </div>
              </td></tr>
            )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {modal && createPortal(
        <div className="crm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="crm-modal crm-modal-lg" role="dialog" aria-modal="true">
            <div className="crm-modal-header">
              <h3>{modal === 'new' ? 'New Contact' : 'Edit Contact'}</h3>
              <button type="button" className="crm-modal-close" onClick={() => setModal(null)} aria-label="Close dialog"><FaTimes aria-hidden="true" /></button>
            </div>
            <div className="crm-modal-body">
              <div className="crm-form-grid">
                {fields.map((f) => (
                  <div className="crm-field" key={f.k}>
                    <label>{f.label} {f.req && <span className="req">*</span>}</label>
                    {f.type === 'select' ? (
                      <AppDropdown value={form[f.k] || ''} options={f.options.map((o) => ({ value: o, label: o }))}
                        onChange={(v) => setForm((p) => ({ ...p, [f.k]: v }))}
                        menuWidth={200} menuClassName="crm-dropdown-window" />
                    ) : f.type === 'checkbox' ? (
                      <label className="crm-check-row">
                        <input type="checkbox" checked={!!form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.checked })} />
                        This is the primary contact
                      </label>
                    ) : (
                      <input type={f.type || 'text'} value={form[f.k] || ''} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="crm-modal-footer">
              <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button type="button" className="crm-btn crm-btn-primary" onClick={save}>{modal === 'new' ? 'Create' : 'Save'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
